import { useState } from 'react';

export interface FlagsState<T> {
	isSet(val: T): boolean;
	set(item: T, val: boolean): void;
	setValues(items: T[]): void;
	getAll(): T[];
	clear(): void;
}

/**
 * This is a custom hook that is designed for keeping track of a list of "flags" or grouped values.
 * It helps avoid creating own state for every value, which is useful especially if the "whole set"
 * is the interesting part.
 * Contains convenience methods for checking if item exists, setting it, getting all values and clearing list
 * @param initialState
 */
export function useFlagsState<T>(initialState: T[] = []): FlagsState<T> {
	const [state, setState] = useState<T[]>(initialState);

	function isSet(item: T): boolean {
		return state.find((f) => f === item) !== undefined;
	}

	function getAll(): T[] {
		return state;
	}

	function set(item: T, val: boolean) {
		return setState((state) => {
			const others = state.filter((v) => v != item);
			return val ? others.concat(item) : others;
		});
	}

	function setValues(items: T[]) {
		return setState(items);
	}

	function clear() {
		setState([]);
	}

	return { isSet, set, setValues, getAll, clear };
}
